<template>
  <div class="background">
    <NavBar />

    <div class="tabs">
      <button type="button" class="tab" :class="{ active: tab === 'mine' }" @click="showTab('mine')">{{ t('list-tab-mine') }}</button>
      <button type="button" class="tab" :class="{ active: tab === 'all' }" @click="showTab('all')">{{ t('list-tab-all') }}</button>
    </div>

    <p v-if="list === null" class="loading">{{ t('page-loading') }}</p>
    <template v-else>
      <!-- My games (SPEC §4.4): sent invitations, then my move, waiting, finished. -->
      <template v-if="tab === 'mine'">
        <section v-if="invitations.length" class="invitations">
          <h4>{{ t('list-invitations') }} ({{ invitations.length }})</h4>
          <router-link v-for="inv in invitations" :key="inv.id" :to="{ name: 'invitation', params: { id: inv.id } }" class="card invitation">
            <span class="line1">
              <span class="who"><span v-if="inviterColour(inv)" class="stone" :class="inviterColour(inv)"></span><span class="quiet">{{ t('list-invitation-waiting') }}</span></span>
              <span class="grid">{{ settingsText(inv.game.setup) }}</span>
            </span>
            <span class="line2">{{ t('list-invitation-left', { days: daysLeft(inv) }) }} · <span role="button" tabindex="0" class="copy" @click.prevent.stop="copyInvitation(inv)">{{ copied === inv.id ? t('invitation-link-copied') : t('list-invitation-copy') }}</span></span>
          </router-link>
        </section>

        <section v-if="yourMove.length" class="your-move">
          <h4>{{ t('list-section-your-move') }} ({{ yourMove.length }})</h4>
          <router-link v-for="g in yourMove" :key="g.id" :to="gameLink(g)" class="card mine">
            <span class="line1"><PlayerName class="who" v-bind="opponent(g)" /><span class="grid">{{ settingsText(g.setup) }}</span></span>
            <span class="line2"><b class="your">{{ t('list-your-move') }}</b>{{ details(g) }}</span>
          </router-link>
        </section>

        <section v-if="waiting.length" class="waiting">
          <h4>{{ t('list-section-waiting') }} ({{ waiting.length }})</h4>
          <router-link v-for="g in waiting" :key="g.id" :to="gameLink(g)" class="card quiet-card">
            <span class="line1"><PlayerName class="who" v-bind="opponent(g)" /><span class="grid">{{ settingsText(g.setup) }}</span></span>
            <span class="line2">{{ t('list-waiting-for', { name: opponentName(g) }) }}{{ details(g) }}</span>
          </router-link>
        </section>
      </template>

      <!-- All games: in progress, then finished. -->
      <section v-else-if="inProgress.length" class="in-progress">
        <h4>{{ t('list-section-in-progress') }} ({{ inProgress.length }})</h4>
        <router-link v-for="g in inProgress" :key="g.id" :to="gameLink(g)" class="row">
          <span class="players"><PlayerName colour="black" :player="seatsOf(g).black" /><span class="vs">vs</span><PlayerName colour="white" :player="seatsOf(g).white" /></span>
          <span class="date">{{ timeAgo(lastTime(g)) }}</span>
        </router-link>
      </section>

      <section v-if="finished.length" class="finished">
        <h4>{{ t('list-section-finished') }} ({{ finished.length }})</h4>
        <router-link v-for="g in finished.slice(0, shown)" :key="g.id" :to="gameLink(g)" class="row">
          <span v-if="tab === 'mine'" class="players"><PlayerName v-bind="opponent(g)" /></span>
          <span v-else class="players"><PlayerName colour="black" :player="seatsOf(g).black" /><span class="vs">vs</span><PlayerName colour="white" :player="seatsOf(g).white" /></span>
          <span class="result" :class="resultClass(g)">{{ gameStatus(g) }}</span>
          <span class="date">{{ date(finishTime(g)) }}</span>
        </router-link>
        <p v-if="finished.length > shown" ref="more" class="more" @click="showMore">{{ t('list-more', { n: finished.length - shown }) }}</p>
        <p v-else-if="finished.length > batch" class="more">{{ t('list-end') }}</p>
      </section>

      <p v-if="empty" class="empty">{{ t('list-empty') }}</p>
    </template>
  </div>
</template>

<script>
import NavBar from './NavBar';
import PlayerName from './PlayerName';
import api from '@/api/api';
import text, { handicapText } from '@/text';
import { t } from '@/texts';
import { seats } from '@/players';
import { copyText } from '@/utils';

// Finished games are shown this many at a time, more as the list is scrolled to its end.
const BATCH = 20;
const DAY = 24 * 3600 * 1000;

export default {
  name: 'GameListPage',

  components: { NavBar, PlayerName },

  data() {
    return {
      tab: 'mine',
      list: null,
      invitations: [],
      shown: BATCH,
      batch: BATCH,
      copied: null,
    };
  },

  computed: {
    me() {
      return this.$store.state.user;
    },
    // Games in progress waiting for my move, the one waited on longest first.
    yourMove() {
      return this.byTime(this.inProgress.filter(g => this.isMyMove(g)), 1);
    },
    waiting() {
      return this.inProgress.filter(g => !this.isMyMove(g));
    },
    // Games in progress, the most recent move first.
    inProgress() {
      return this.byTime((this.list || []).filter(g => g.status !== 'completed'), -1);
    },
    finished() {
      const list = (this.list || []).filter(g => g.status === 'completed');
      return list.sort((a, b) => this.finishTime(b) - this.finishTime(a) || b.id - a.id);
    },
    empty() {
      return !this.inProgress.length && !this.finished.length && !(this.tab === 'mine' && this.invitations.length);
    },
  },

  methods: {
    t,

    seatsOf(g) {
      return seats(g);
    },

    // Whether the game waits for the signed-in player's move.
    isMyMove(g) {
      if(!this.me || g.status !== 'in-progress' || !g.nextStone) return false;
      const player = seats(g)[g.nextStone];
      return !!player && player.id === this.me.id;
    },

    // The other player of one of my games, with their colour; "you" when I play both.
    opponent(g) {
      const s = seats(g);
      const me = this.me && this.me.id;
      if(s.black && s.white && s.black.id === me && s.white.id === me) return { colour: 'white', player: s.white, you: true };
      if(s.black && s.black.id === me) return { colour: 'white', player: s.white };
      return { colour: 'black', player: s.black };
    },

    opponentName(g) {
      const o = this.opponent(g);
      return o.you ? t('seat-you') : o.player ? o.player.displayName : t('seat-empty');
    },

    // " · move 31 · 3 hours ago" after a card's status.
    details(g) {
      const parts = [];
      if(g.moveCount) parts.push(t('list-move-number', { n: g.moveCount }));
      const ago = this.timeAgo(this.lastTime(g));
      if(ago) parts.push(ago);
      return parts.map(p => ` · ${p}`).join('');
    },

    lastTime(g) {
      return new Date(g.lastMoveAt || g.createdAt || 0).getTime();
    },

    finishTime(g) {
      const done = g.completedAt && !g.completedAt.startsWith('0001') ? g.completedAt : null;
      return new Date(done || g.lastMoveAt || g.createdAt || 0).getTime();
    },

    byTime(list, order) {
      return list.slice().sort((a, b) => order * (this.lastTime(a) - this.lastTime(b)) || b.id - a.id);
    },

    timeAgo(time) {
      if(!time) return '';
      const minutes = Math.floor((Date.now() - time) / 60000);
      if(minutes < 1) return t('time-just-now');
      if(minutes < 60) return t('time-minutes-ago', { n: minutes });
      if(minutes < 24 * 60) return t('time-hours-ago', { n: Math.floor(minutes / 60) });
      return t('time-days-ago', { n: Math.floor(minutes / (24 * 60)) });
    },

    // MM-DD this year, YYYY-MM-DD before.
    date(time) {
      if(!time) return '';
      const d = new Date(time);
      const md = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return d.getFullYear() === new Date().getFullYear() ? md : `${d.getFullYear()}-${md}`;
    },

    // Green when I won, red when I lost (my games only).
    resultClass(g) {
      if(this.tab !== 'mine' || !g.winner) return null;
      const s = seats(g);
      const winner = s[g.winner];
      if(!winner || (s.black && s.white && s.black.id === s.white.id)) return null;
      return winner.id === this.me.id ? 'won' : 'lost';
    },

    // A card's settings (SPEC §4.4): the board size when it isn't the usual 19, then the handicap.
    settingsText(setup) {
      const parts = [];
      if(setup.grid && setup.grid !== 19) parts.push(t('list-grid', { grid: setup.grid }));
      if(setup.handicap) parts.push(handicapText(setup.handicap));
      return parts.join(' ');
    },

    gameStatus(g) {
      return text.status(g);
    },

    gameLink(g) {
      return { name: 'game', params: { id: g.id } };
    },

    // The inviter's colour, when it is known before the invitation is accepted.
    inviterColour(inv) {
      const setup = inv.game.setup;
      if(setup.blackPlayer) return setup.blackPlayer === this.me.id ? 'black' : 'white';
      return setup.nigiri ? null : 'white';
    },

    daysLeft(inv) {
      return Math.max(1, Math.ceil((new Date(inv.expiresAt).getTime() - Date.now()) / DAY));
    },

    async copyInvitation(inv) {
      await copyText(`${window.location.origin}/invitations/${inv.id}`);
      this.copied = inv.id;
    },

    // The viewer's open invitations, shown above their games (SPEC §4.4).
    async loadInvitations() {
      try {
        this.invitations = await api.call('GET', `invitations?createdBy=${this.me.id}`);
      } catch(e) {
        this.invitations = [];
      }
    },

    async showTab(tab) {
      if(this.tab !== tab) { this.tab = tab; this.list = null; }
      this.shown = BATCH;
      const list = await api.getList(api.GAMES, tab === 'mine' ? { player: this.me.id } : {});
      if(this.tab !== tab) return;
      this.list = list;
    },

    showMore() {
      this.shown += BATCH;
    },

    // Near the end of the page, the next batch of finished games is shown.
    onScroll() {
      const more = this.$refs.more;
      if(more && more.getBoundingClientRect().top < window.innerHeight + 100) this.showMore();
    },
  },

  async created() {
    this.loadInvitations();
    await this.showTab('mine');
  },

  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
}
</script>

<style scoped>
.tabs {
  display: flex;
  gap: 6px;
  max-width: 600px;
  margin: 8px auto 0;
  padding: 0 10px;
}

.tab {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #ccc;
  border-radius: 16px;
  background: #fff;
  color: #2c3e50;
  font-size: 14px;
  cursor: pointer;
}

.tab.active {
  background: #ffd39a;
  border-color: #e0b060;
  font-weight: bold;
}

section {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 10px;
  text-align: left;
}

h4 {
  margin: 14px 0 6px;
  font-size: 14px;
  color: #555;
}

.loading, .empty {
  color: #999;
}

/* A game in progress (or a sent invitation): the whole card is a link. */
.card {
  display: block;
  box-sizing: border-box;
  min-height: 64px;
  margin-bottom: 8px;
  padding: 9px 12px;
  border: 1px solid #d8c9a6;
  border-radius: 8px;
  background: #fffaf0;
  color: #2c3e50;
  text-decoration: none;
}

.card .line1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 17px;
}

.card .who {
  min-width: 0;
}

.card .grid {
  color: #999;
  flex: none;
}

.card .line2 {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: #777;
}

.card .your {
  color: #0175ff;
}

.card.quiet-card {
  background: #fafafa;
  border-color: #e3e3e3;
  color: #666;
}

/* An invitation is a game still waiting for its second player: dashed. */
.card.invitation {
  background: #fff;
  border-style: dashed;
}

.card .quiet {
  color: #999;
}

.card .copy {
  color: #8b5e34;
  text-decoration: none;
}

/* A finished game, or any game in "all games": a compact row. */
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #2c3e50;
  text-decoration: none;
}

.row .players {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.row .vs {
  color: #999;
  margin: 0 6px;
  font-size: 12px;
}

.row .result {
  white-space: nowrap;
}

.row .result.won {
  color: #0a0;
}

.row .result.lost {
  color: #d00;
}

.row .date {
  width: 64px;
  flex: none;
  color: #999;
  font-size: 12px;
  text-align: right;
}

.more {
  margin: 10px 0 20px;
  color: #999;
  font-size: 13px;
  text-align: center;
  cursor: pointer;
}
</style>
